import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const NAV_ITEMS = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Fleet", to: "/fleet" },
    { label: "Careers", to: "/careers" },
    { label: "Contact", to: "/contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#040914]/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="wms-container flex items-center justify-between h-28">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-4 group">
                    <span className="relative flex items-center justify-center w-28 h-28 bg-white p-1.5 shadow-[0_4px_20px_rgba(72,202,228,0.15)]">
                        <img
                            src="https://customer-assets.emergentagent.com/job_windermere-shipping/artifacts/1re155dq_Minimalist%20Logo%20Design%20for%20Windermere%20Shipping%20Pte.png"
                            alt="Windermere Shipping Pte Ltd logo"
                            className="w-full h-full object-contain"
                        />
                    </span>
                    <div className="flex flex-col leading-none">
                        <span className="font-display text-[15px] tracking-[0.18em] uppercase text-white">Windermere</span>
                        <span className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#48CAE4]/80">Shipping · Pte Ltd</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `relative px-4 py-2 text-sm tracking-wide font-sans transition-colors ${
                                    isActive ? "text-white" : "text-white/55 hover:text-white"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <span className="inline-flex flex-col">
                                    {item.label}
                                    <span
                                        className={`mt-1 h-px transition-all duration-300 ${
                                            isActive ? "w-full bg-[#48CAE4]" : "w-0 bg-[#48CAE4]"
                                        }`}
                                    />
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link
                        to="/contact?intent=charter"
                        data-testid="navbar-charter-btn"
                        className="px-5 py-3 text-xs uppercase tracking-[0.22em] font-medium bg-white text-[#040914] hover:bg-[#48CAE4] transition-colors"
                    >
                        Request Charter
                    </Link>
                </div>

                <button
                    type="button"
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/15 text-white"
                >
                    {open ? <X size={20} /> : <List size={20} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="lg:hidden border-t border-white/10 bg-[#040914]/95 backdrop-blur-xl"
                        data-testid="mobile-nav"
                    >
                        <div className="wms-container py-6 flex flex-col gap-1">
                            {NAV_ITEMS.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `px-3 py-3 text-sm uppercase tracking-[0.18em] border-l-2 ${
                                            isActive
                                                ? "border-[#48CAE4] text-white bg-white/5"
                                                : "border-transparent text-white/60"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/contact?intent=charter"
                                data-testid="mobile-charter-btn"
                                className="mt-4 px-5 py-4 text-xs uppercase tracking-[0.22em] bg-white text-[#040914] text-center"
                            >
                                Request Charter
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
